const EDGE_CONDITIONS = new Set(['completed', 'output_available'])

const WORK_ITEM_WRITE_FIELDS = [
  'name',
  'item_type',
  'required',
  'completion_policy',
  'due_rule',
  'assignees',
  'input_config',
  'notification_rule',
  'rpa_item_id',
  'rpa_input_mapping',
  'rpa_output_mapping',
]

const clone = value => {
  if (value === undefined) return undefined
  return JSON.parse(JSON.stringify(value))
}

const nodeKey = value => String(value || '')

const nodeCanvasId = (node = {}, index = 0) => String(
  node.canvas_id
  ?? node.work_item?.id
  ?? node.runtime_definition?.id
  ?? node.local_id
  ?? node.id
  ?? node.client_key
  ?? `canvas-${index}`
)

const assignmentUserId = assignment => assignment?.user_id ?? assignment?.id ?? assignment?.value

const canvasAssignmentState = (assignments = []) => {
  const rows = Array.isArray(assignments) ? assignments : []
  const assignee = rows.find(row => row?.role === 'assignee' || !row?.role)
  const assigneeId = assignmentUserId(assignee)
  return {
    default_assignee_id: assigneeId ?? '',
    watcher_ids: rows
      .filter(row => row?.role === 'watcher')
      .map(assignmentUserId)
      .filter(value => value !== undefined && value !== null && value !== ''),
  }
}

const writeAssignments = (assigneeId, watcherIds = []) => {
  const hasAssignee = assigneeId !== undefined && assigneeId !== null && assigneeId !== ''
  const assigneeKey = hasAssignee ? String(assigneeId) : ''
  const seen = new Set()
  const watchers = (Array.isArray(watcherIds) ? watcherIds : []).filter(value => {
    if (value === undefined || value === null || value === '') return false
    const key = String(value)
    if (key === assigneeKey || seen.has(key)) return false
    seen.add(key)
    return true
  })
  return [
    ...(hasAssignee ? [{ user_id: assigneeId, role: 'assignee', notify_enabled: true }] : []),
    ...watchers.map(userId => ({ user_id: userId, role: 'watcher', notify_enabled: true })),
  ]
}

const normalizedLayout = (layout, index = 0) => ({
  x: Number.isFinite(Number(layout?.x)) ? Number(layout.x) : index % 4,
  y: Number.isFinite(Number(layout?.y)) ? Number(layout.y) : Math.floor(index / 4),
  ...(Number.isFinite(Number(layout?.w)) ? { w: Number(layout.w) } : {}),
  ...(Number.isFinite(Number(layout?.h)) ? { h: Number(layout.h) } : {}),
})

export function normalizeTemplateGraph(template = {}) {
  const source = template.graph && typeof template.graph === 'object'
    ? template.graph
    : template
  return {
    tree_version: Number(source.tree_version ?? template.tree_version) || 0,
    nodes: (Array.isArray(source.nodes) ? source.nodes : []).map((node, index) => ({
      ...clone(node),
      canvas_id: nodeCanvasId(node, index),
      client_key: nodeKey(node.client_key),
      node_type: node.node_type === 'runtime_definition' ? 'runtime_definition' : 'work_item',
      order: Number.isInteger(node.order) ? node.order : index,
      layout: normalizedLayout(node.layout, index),
      ...(node.work_item ? { work_item: clone(node.work_item) } : {}),
      ...(node.runtime_definition ? { runtime_definition: clone(node.runtime_definition) } : {}),
      migration_issues: Array.isArray(node.migration_issues) ? clone(node.migration_issues) : [],
    })),
    edges: (Array.isArray(source.edges) ? source.edges : []).map(edge => ({
      from_node_key: nodeKey(edge.from_node_key),
      to_node_key: nodeKey(edge.to_node_key),
      condition: EDGE_CONDITIONS.has(edge.condition) ? edge.condition : 'completed',
    })),
  }
}

export function templateGraphCanvasItems(graph = {}) {
  return (Array.isArray(graph.nodes) ? graph.nodes : []).map((node, index) => {
    const layout = normalizedLayout(node.layout, index)
    const canvasId = nodeCanvasId(node, index)
    if (node.node_type === 'runtime_definition') {
      const runtime = node.runtime_definition || {}
      const branchTemplates = Array.isArray(runtime.payload?.branch_templates)
        ? runtime.payload.branch_templates
        : []
      return {
        ...clone(node),
        canvas_id: canvasId,
        id: node.client_key,
        local_id: node.client_key,
        item_type: 'runtime_definition',
        name: runtime.name || node.name || 'Rule 설정',
        branch_template_count: branchTemplates.length,
        x: layout.x,
        y: layout.y,
        w: layout.w || 1,
        h: layout.h || 1,
      }
    }
    return {
      ...clone(node.work_item || {}),
      ...clone(node),
      ...canvasAssignmentState(node.work_item?.assignees),
      canvas_id: canvasId,
      id: node.client_key,
      local_id: node.client_key,
      x: layout.x,
      y: layout.y,
      w: layout.w || 1,
      h: layout.h || 1,
    }
  })
}

export function templateGraphCanvasFlows(graph = {}) {
  return (Array.isArray(graph.edges) ? graph.edges : []).map((edge, index) => ({
    ...clone(edge),
    id: `${edge.from_node_key}:${edge.condition}:${edge.to_node_key}:${index}`,
    start_item_id: edge.from_node_key,
    end_item_id: edge.to_node_key,
    start_item_position: edge.start_item_position || 'right',
    end_item_position: edge.end_item_position || 'left',
  }))
}

export function runtimeDefinitionCollisionKeys(items = []) {
  const keys = []
  for (const item of Array.isArray(items) ? items : []) {
    if (item?.client_key) keys.push(String(item.client_key))
    if (item?.node_type === 'runtime_definition' && item.runtime_definition?.key) {
      keys.push(String(item.runtime_definition.key))
    }
  }
  return [...new Set(keys)]
}

function workItemWriteBody(item = {}) {
  const source = { ...(item.work_item || {}), ...item }
  const body = Object.fromEntries(WORK_ITEM_WRITE_FIELDS
    .filter(key => source[key] !== undefined)
    .map(key => [key, clone(source[key])]))
  if (
    Object.prototype.hasOwnProperty.call(source, 'default_assignee_id')
    || Object.prototype.hasOwnProperty.call(source, 'watcher_ids')
  ) {
    body.assignees = writeAssignments(source.default_assignee_id, source.watcher_ids)
  }
  return body
}

export function buildTemplateGraphPayload({ graph = {}, expectedTreeVersion } = {}) {
  return {
    expected_tree_version: expectedTreeVersion,
    nodes: (Array.isArray(graph.nodes) ? graph.nodes : []).map((node, index) => {
      const common = {
        client_key: nodeKey(node.client_key),
        node_type: node.node_type,
        layout: clone(node.layout || normalizedLayout(null, index)),
        order: Number.isInteger(node.order) ? node.order : index,
      }
      if (node.node_type === 'runtime_definition') {
        return {
          ...common,
          runtime_definition: { id: node.runtime_definition?.id },
          trigger_mode: node.trigger_mode || 'auto',
          completion_mode: node.completion_mode || 'single_exit',
        }
      }
      return {
        ...common,
        node_type: 'work_item',
        work_item: workItemWriteBody(node),
      }
    }),
    edges: (Array.isArray(graph.edges) ? graph.edges : []).map(edge => ({
      from_node_key: nodeKey(edge.from_node_key),
      to_node_key: nodeKey(edge.to_node_key),
      condition: EDGE_CONDITIONS.has(edge.condition) ? edge.condition : 'completed',
    })),
  }
}

export function replaceTemplateGraphFromCanvas(graph = {}, change = {}) {
  const previousByClientKey = new Map((graph.nodes || []).map(node => [node.client_key, node]))
  const previousByCanvasId = new Map((graph.nodes || []).map((node, index) => [nodeCanvasId(node, index), node]))
  const nodes = (change.items || []).map((item, index) => {
    const key = nodeKey(item.client_key || item.local_id || item.id)
    const canvasId = String(item.canvas_id ?? item.i ?? item.local_id ?? item.id ?? key)
    const prior = previousByCanvasId.get(canvasId) || previousByClientKey.get(key) || {}
    const layout = {
      x: Number(item.x) || 0,
      y: Number(item.y) || 0,
      ...(Number(item.w) > 1 ? { w: Number(item.w) } : {}),
      ...(Number(item.h) > 1 ? { h: Number(item.h) } : {}),
    }
    if (item.node_type === 'runtime_definition' || prior.node_type === 'runtime_definition') {
      return {
        ...clone(prior),
        ...clone(item),
        canvas_id: canvasId,
        client_key: key,
        node_type: 'runtime_definition',
        order: index,
        layout,
        runtime_definition: clone(item.runtime_definition || prior.runtime_definition || {}),
      }
    }
    return {
      ...clone(prior),
      canvas_id: canvasId,
      client_key: key,
      node_type: 'work_item',
      order: index,
      layout,
      work_item: workItemWriteBody(item),
    }
  })
  const edges = (change.flows || []).map(flow => ({
    from_node_key: nodeKey(flow.from_node_key || flow.start_item_id || flow.from_item_id),
    to_node_key: nodeKey(flow.to_node_key || flow.end_item_id || flow.to_item_id),
    condition: EDGE_CONDITIONS.has(flow.condition) ? flow.condition : 'completed',
  }))
  return {
    tree_version: Number(graph.tree_version) || 0,
    nodes,
    edges,
  }
}

function graphHasCycle(nodes, edges) {
  const keys = new Set(nodes.map(node => node.client_key))
  const outgoing = new Map([...keys].map(key => [key, []]))
  for (const edge of edges) {
    if (keys.has(edge.from_node_key) && keys.has(edge.to_node_key)) {
      outgoing.get(edge.from_node_key).push(edge.to_node_key)
    }
  }
  const visiting = new Set()
  const visited = new Set()
  const visit = key => {
    if (visiting.has(key)) return true
    if (visited.has(key)) return false
    visiting.add(key)
    for (const target of outgoing.get(key) || []) {
      if (visit(target)) return true
    }
    visiting.delete(key)
    visited.add(key)
    return false
  }
  return [...keys].some(visit)
}

export function templateGraphValidationErrors(graph = {}) {
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : []
  const edges = Array.isArray(graph.edges) ? graph.edges : []
  const byKey = new Map(nodes.map(node => [node.client_key, node]))
  const errors = []
  const runtimeExitCounts = new Map()

  for (const edge of edges) {
    const source = byKey.get(edge.from_node_key)
    const target = byKey.get(edge.to_node_key)
    if (!source || !target) {
      errors.push({ code: 'TEMPLATE_GRAPH_ENDPOINT_NOT_FOUND', edge: clone(edge) })
      continue
    }
    if (edge.from_node_key === edge.to_node_key) {
      errors.push({ code: 'TEMPLATE_GRAPH_SELF_EDGE', edge: clone(edge) })
    }
    if (!EDGE_CONDITIONS.has(edge.condition)) {
      errors.push({ code: 'TEMPLATE_GRAPH_EDGE_CONDITION_UNSUPPORTED', edge: clone(edge) })
    }
    if (edge.condition === 'output_available' && source.node_type !== 'runtime_definition') {
      errors.push({ code: 'TEMPLATE_GRAPH_OUTPUT_SOURCE_INVALID', edge: clone(edge) })
    }
    if (source.node_type === 'runtime_definition') {
      const exitKey = `${edge.from_node_key}:${edge.condition}`
      runtimeExitCounts.set(exitKey, (runtimeExitCounts.get(exitKey) || 0) + 1)
    }
  }

  for (const [exitKey, count] of runtimeExitCounts) {
    if (count > 1) {
      const [runtime_node_key, condition] = exitKey.split(':')
      errors.push({
        code: 'TEMPLATE_GRAPH_RUNTIME_EXIT_AMBIGUOUS',
        runtime_node_key,
        condition,
      })
    }
  }
  if (graphHasCycle(nodes, edges)) {
    errors.push({ code: 'TEMPLATE_GRAPH_CYCLE' })
  }
  return errors
}

export function runtimeMigrationActions(node = {}) {
  return (Array.isArray(node.migration_issues) ? node.migration_issues : []).map(issue => {
    const allowedFrom = issue.fix?.allowed_from_node_keys
    const allowedTo = issue.fix?.allowed_to_node_keys
    return {
      action: issue.fix?.action || null,
      direction: Array.isArray(allowedFrom) ? 'incoming' : 'outgoing',
      allowed_node_keys: clone(allowedFrom || allowedTo || []),
      definition_path: issue.definition_path || '/',
      description: issue.fix?.description || issue.message || '',
      issue,
    }
  })
}

export function runtimeOutgoingConnectionReview({ nodeKey: runtimeNodeKey, nodes = [], edges = [], issue = {} } = {}) {
  if (!String(issue.code || '').startsWith('RUNTIME_GRAPH_OUTGOING_')) return null
  const key = nodeKey(runtimeNodeKey)
  const nodeNames = new Map((Array.isArray(nodes) ? nodes : []).map(node => [
    nodeKey(node.client_key),
    node.work_item?.name || node.runtime_definition?.name || node.name || nodeKey(node.client_key),
  ]))
  const outgoing = (Array.isArray(edges) ? edges : []).filter(edge => (
    nodeKey(edge.from_node_key || edge.start_item_id) === key
  ))
  const outputEdges = outgoing.filter(edge => edge.condition === 'output_available')
  const targetNames = rows => rows.map(edge => {
    const targetKey = nodeKey(edge.to_node_key || edge.end_item_id)
    return nodeNames.get(targetKey) || targetKey
  })
  const base = {
    location: 'Main canvas > Rule 연결',
    targets: targetNames(outputEdges),
    candidates: [],
    canStartEdgeFix: false,
  }
  const canConnectGraphEdge = issue.fix?.action === 'connect_graph_edge'
  const issueCandidates = Array.isArray(issue.candidates) ? issue.candidates : []
  const allowedCandidates = Array.isArray(issue.fix?.allowed_to_node_keys)
    ? issue.fix.allowed_to_node_keys
    : []
  const candidateKeys = canConnectGraphEdge
    ? [...new Set((issueCandidates.length ? issueCandidates : allowedCandidates)
        .map(candidate => nodeKey(candidate))
        .filter(Boolean))]
    : []
  const candidates = candidateKeys.map(candidate => nodeNames.get(candidate) || candidate)
  if (outputEdges.length > 1) {
    return {
      state: 'multiple_output_edges',
      title: 'Runtime 출력 연결 정리 필요',
      message: canConnectGraphEdge
        ? "'Runtime 출력 준비 시' 연결은 하나만 유지해야 합니다. Rule Draft 지정 대상에 연결되도록 정리하세요."
        : "'Runtime 출력 준비 시' 연결은 하나만 유지해야 합니다. 불필요한 연결을 제거한 뒤 템플릿을 저장하세요.",
      ...base,
      candidates,
      canStartEdgeFix: canConnectGraphEdge,
    }
  }
  if (canConnectGraphEdge) {
    const candidateKeySet = new Set(candidateKeys)
    const outputTargetKeys = outputEdges.map(edge => nodeKey(edge.to_node_key || edge.end_item_id))
    if (outputTargetKeys.length === 1 && candidateKeySet.has(outputTargetKeys[0])) {
      return {
        state: 'graph_edge_ready',
        title: 'Runtime 출력 연결 저장 필요',
        message: '현재 Runtime 출력 연결이 Rule Draft 지정 대상과 일치합니다. 템플릿을 저장해 연결 검토 상태를 갱신하세요.',
        ...base,
        location: 'Main canvas > Template 저장',
        candidates,
      }
    }
    const targetMismatch = outputEdges.length > 0
    return {
      state: 'graph_edge_missing',
      title: targetMismatch ? '후행 할일 연결 대상 확인 필요' : '후행 할일 연결 필요',
      message: targetMismatch
        ? "현재 Runtime 출력 연결 대상이 Rule Draft 지정 대상과 다릅니다. 'Runtime 출력 준비 시' 연결 대상을 확인하세요."
        : "Rule Draft가 지정한 후행 할일을 Main canvas에서 'Runtime 출력 준비 시'로 연결하세요.",
      ...base,
      candidates,
      canStartEdgeFix: true,
    }
  }
  if (outputEdges.length > 0) {
    return {
      state: 'draft_sync_required',
      title: 'Rule 연결 동기화 필요',
      message: 'Main canvas 연결은 있지만 Rule Draft의 Join 대상과 일치하지 않습니다. 템플릿을 저장하면 현재 Runtime 출력 연결을 Draft에 자동 동기화합니다.',
      ...base,
      location: 'Main canvas > Template 저장',
    }
  }
  if (outgoing.length > 0) {
    const targets = targetNames(outgoing)
    return {
      state: 'wrong_condition',
      title: 'Runtime 출력 연결 필요',
      message: `'${targets.join(', ')}'에는 'Runtime 출력 준비 시' 연결이 없습니다. 기존 완료 연결이 필요하면 유지하고, 결과 후행 할일에는 Runtime 출력 연결을 추가하세요.`,
      ...base,
      targets,
    }
  }
  return {
    state: 'missing_edge',
    title: '후행 할일 연결 필요',
    message: "Main canvas에서 Rule과 후행 할일을 연결하고 연결 조건을 'Runtime 출력 준비 시'로 설정하세요.",
    ...base,
  }
}

export function applyRuntimeMigrationEdgeFix({ graph = {}, nodeKey: runtimeNodeKey, issue = {}, targetNodeKey } = {}) {
  const action = runtimeMigrationActions({ migration_issues: [issue] })[0]
  if (!action || action.action !== 'connect_graph_edge') {
    throw new Error('Migration issue does not support a graph edge fix.')
  }
  if (!action.allowed_node_keys.includes(targetNodeKey)) {
    throw new Error('Target node is not allowed for this migration fix.')
  }
  const incoming = action.direction === 'incoming'
  const condition = incoming ? 'completed' : 'output_available'
  const edges = (graph.edges || []).filter(edge => incoming
    ? !(edge.to_node_key === runtimeNodeKey && edge.condition === condition)
    : !(edge.from_node_key === runtimeNodeKey && edge.condition === condition))
  edges.push(incoming
    ? { from_node_key: targetNodeKey, to_node_key: runtimeNodeKey, condition }
    : { from_node_key: runtimeNodeKey, to_node_key: targetNodeKey, condition })
  return {
    ...clone(graph),
    edges,
  }
}

function runtimeEdgeReferences(graph, runtimeNodeKey) {
  const incoming = (graph.edges || []).filter(edge => (
    edge.to_node_key === runtimeNodeKey && edge.condition === 'completed'
  ))
  const outgoing = (graph.edges || []).filter(edge => (
    edge.from_node_key === runtimeNodeKey && edge.condition === 'output_available'
  ))
  return {
    source_item_client_key: incoming.length === 1 ? incoming[0].from_node_key : null,
    output_target_work_item_client_key: outgoing.length === 1 ? outgoing[0].to_node_key : null,
  }
}

export function runtimeGraphReferenceChanges({ before = {}, after = {} } = {}) {
  return (after.nodes || [])
    .filter(node => node.node_type === 'runtime_definition')
    .flatMap(node => {
      const previous = runtimeEdgeReferences(before, node.client_key)
      const next = runtimeEdgeReferences(after, node.client_key)
      if (
        previous.source_item_client_key === next.source_item_client_key
        && previous.output_target_work_item_client_key === next.output_target_work_item_client_key
      ) {
        return []
      }
      return [{
        node_key: node.client_key,
        definition_id: node.runtime_definition?.id || null,
        ...next,
      }]
    })
}

export function templatePublishBlockers(graph = {}) {
  return (graph.nodes || [])
    .filter(node => node.node_type === 'runtime_definition' && node.migration_status === 'needs_review')
    .map(node => ({
      node_key: node.client_key,
      code: 'RUNTIME_GRAPH_MIGRATION_REVIEW_REQUIRED',
      message: 'Rule 연결을 확인해 주세요.',
    }))
}
